import { fetchPage } from "../../fetch/graphql/page";
import { fetchSingleLineup } from "../../fetch/graphql/requests/lineups";
import { page_initial_data } from "../../reducers/Page/reducer";
import { update_page_sections } from "./dispatchers";
import { app_reset } from "../../reducers/App/reducer";
import { setAppState } from "../App/dispatchers";
import { set_loading_complete } from "../Loader/dispatchers";
import { start_loading } from "../../reducers/Loader/reducer";
import { fetch_tracking } from "../Tracking/dispatchers";
import {
    fetchLineupVariant,
    getABTestsConfig,
    getABTestFromTestId,
    getLineupSwap,
} from "./utils";
import { parsePageSections } from "../../utils/dataTransform";
import { transformAllContentItems } from "../../utils/dataTransform/library";
import { injectNativeAds } from "../../pages/FlexibleLandingPage/components/AdElementContainer/component";
import {
    calculateTotalCards,
    checkForNativeAd,
} from "../../utils/dataTransform/utils";
import log from "../../utils/logger/library";
import { GqlError } from "../../utils/errors/http";
import { LOADMORE_AD_TYPE } from "../../components/Ad/constants";
import { DEFAULT_PAGE_SIZE } from "../../constants/content";
import { getFeedCookie } from "../../utils/cookies/library";
import { DEFAULT_AB_VARIANT } from "./constants";
import { mainSchedulePageReducer } from "../../components/Schedule/components/MainSchedule/utils";
import { fetch_newsletters_if_needed } from "../Newsletters/actions";
import { fetch_single_lineup_for_path } from "../../actions/SingleLineup/dispatchers";
/**
 * Async action creator to fetch lineupSwap server side, given the testId from akamaiHeader
 * @param {string} path of current page to load
 * @param {Object} akamaiHeader
 * @return {function} dispatch set_lineup_variant action
 */
export const set_lineup_variant = (path, akamaiHeader) => {
    return async (dispatch) => {
        // Do not store swap for preview page
        if (path.indexOf("meta/nine-preview/") === 0) {
            return;
        }

        // Default lineupSwap value -- reflects pages that do not have AB testing enabled or is assigned variant A
        const defaultLineupSwapObject = {
            [path]: null,
        };

        const defaultPageDispatcher = dispatch(
            page_initial_data({
                page: { path },
                lineupSwapObject: defaultLineupSwapObject,
            })
        );

        const variantHeaderKey = Object.keys(akamaiHeader).find(
            (key) => key.toLowerCase() === "x-ab-variant"
        );
        if (!variantHeaderKey) {
            return defaultPageDispatcher;
        }

        const ABTests = await getABTestsConfig();
        if (!ABTests) {
            return defaultPageDispatcher;
        }

        const ABVariant = akamaiHeader[variantHeaderKey];
        if (!ABVariant) {
            return defaultPageDispatcher;
        }

        const [id, selection] = ABVariant.split("|");

        if (id && selection && selection !== DEFAULT_AB_VARIANT) {
            const test = getABTestFromTestId(ABTests, id);

            if (test) {
                const lineupSwapObject = getLineupSwap(selection, test);

                if (lineupSwapObject) {
                    return dispatch(
                        page_initial_data({ page: { path }, lineupSwapObject })
                    );
                }
            }
        }

        return defaultPageDispatcher;
    };
};

/**
 * A thunk action creator for handling data fetching for the page
 * @param {string} path match against featureflags to retrieve the departments to fetch for the page sections
 * @param {boolean} isNinePreview flag indicating whether the page to be fetched is a Nine preview page
 */
export const fetch_page_content = (path, isNinePreview = false) => {
    return async (dispatch, getState) => {
        dispatch(start_loading());
        try {
            const formattedPath = path === "homepage" ? path : path.slice(1);

            let queryPath = "";
            // Nine preview graphQL parameter
            if (isNinePreview) {
                const nine_gtoken = getFeedCookie("nine_gtoken");
                queryPath = `path: "${formattedPath}",contentStatus: "preview;aggToken=${
                    nine_gtoken?.value
                };cacheBust=${new Date().getTime()}"`;
            }
            // Regular landing graphQL parameter
            else {
                queryPath = `path: "${formattedPath}"`;
            }

            // lineupSwapObject has shape { [testScopePath]: {[defaultLineupSlug]: swapLineupSlug} }, to be stored in state.page.lineupSwaps with testScopePath as key
            let lineupSwapObject = {};
            // lineupSwap has shape {[defaultLineupSlug]: swapLineupSlug}, and will be passed into fetchPage
            let lineupSwap = null;

            // AB Testing will not apply for Nine Preview pages
            if (!isNinePreview) {
                // Check if the current path is in the lineupSwaps object in state -- all paths visited during the current navigation session are stored
                // Paths without a lineupSwap have a lineupSwapObject with the shape [path]: null
                const isStoredPath = Object.keys(
                    getState().page.lineupSwaps
                ).includes(formattedPath);

                if (!isStoredPath) {
                    lineupSwapObject = await fetchLineupVariant(formattedPath);
                    lineupSwap = lineupSwapObject[formattedPath];
                } else {
                    lineupSwap = getState().page.lineupSwaps[formattedPath];
                }
            }

            let { page } = await fetchPage(
                queryPath,
                isNinePreview,
                lineupSwap
            );

            // fetches NewsBar on Homepage
            await dispatch(fetch_single_lineup_for_path(path, 4));

            await dispatch(fetch_newsletters_if_needed(page?.sections));

            const navigation =
                page?.navigation?.items?.length > 0 ? page.navigation : {};
            page && dispatch(fetch_tracking(page, false));
            page.sections = page && page.sections && parsePageSections(page);
            page.defaultHeadlineImage =
                page?.section?.defaultHeadlineImage?.aspects?.ratio16x9?.url ||
                "";
            //TODO: Remove ratioCustom fallback once all images have been migrated to AIS
            page.bannerImage =
                page.bannerImage?.aspects?.ratio5x1?.url ||
                page.bannerImage?.aspects?.ratioCustom?.url ||
                "";

            page.initialTotalCards = calculateTotalCards(page.sections);

            // Inject native ad
            const sectionKey = page.path === "sports" ? 1 : 0;
            const sectionContent = page.sections[sectionKey]?.main?.content[0];
            const insertNativeAd = checkForNativeAd(
                page,
                sectionContent,
                sectionKey
            );

            if (insertNativeAd) {
                const gridData =
                    injectNativeAds({
                        params: {
                            gridData: sectionContent.content,
                            sectionIndex: 0,
                            isNativeAd: true,
                        },
                    }) || sectionContent.content;

                sectionContent.content = gridData;
            }
            page = mainSchedulePageReducer(page);
            dispatch(
                page_initial_data({
                    page,
                    inPageNav: { navigation },
                    lineupSwapObject,
                })
            );
        } catch (error) {
            const gqlError =
                error instanceof Error ? error : new GqlError(error);
            log.error(
                `(fetch_page_content) request failed for path ${path}`,
                gqlError
            );
            throw gqlError;
        } finally {
            dispatch(set_loading_complete());
        }
    };
};

export const fetch_page_load_more =
    (loadMoreSizes = {}) =>
    ({
        sectionIndex = 0,
        contentIndex = 0,
        position = "main",
        slug = "",
        pubQueueId,
    }) =>
    ({ page = 2 }) => {
        return async (dispatch) => {
            dispatch(start_loading());

            try {
                const initialTotalCards = loadMoreSizes?.[pubQueueId] || 0;
                const pageSize = initialTotalCards || DEFAULT_PAGE_SIZE;

                const { allContentItems } = await fetchSingleLineup({
                    lineupSlug: slug || "",
                    categorySlug: "empty-category",
                    page,
                    pageSize,
                });

                const metadata = {
                    sectionIndex,
                    contentIndex,
                    position,
                };

                const newContentLength = allContentItems?.nodes?.length ?? 0;
                const newSize =
                    page === 2 ? initialTotalCards + newContentLength : null;

                const loadMore = {
                    newSize,
                    disableLoadMore: newContentLength < pageSize,
                    newContent: transformAllContentItems(
                        allContentItems?.nodes
                    ),
                };
                if (loadMore.newContent[0]) {
                    loadMore.newContent[0].focus = true;
                }
                loadMore.newContent.unshift({ type: LOADMORE_AD_TYPE });
                dispatch(update_page_sections(loadMore, metadata));
            } catch (error) {
                const gqlError =
                    error instanceof Error ? error : new GqlError(error);
                log.error(
                    `(fetch_page_load_more) request failed for section ${slug}`,
                    gqlError
                );
                throw gqlError;
            } finally {
                dispatch(set_loading_complete());
            }
        };
    };

/**
 * A thunk action creator for resetting the app state
 * @param {string} path
 * @param {string} contentId
 */
export const reset_app_state = (path, contentId = "") => {
    return async (dispatch) => {
        dispatch(app_reset());
        dispatch(setAppState(path, false, contentId));
    };
};
